/**
 * The config window: the KDL configuration file, its problems, and an editor.
 *
 * - Errors and warnings are listed with their line; clicking one selects that line.
 * - "Save" sends the text to the core, which checks it: with errors nothing is written and
 *   the reasons are shown; without errors the file is written and every GUI updates at once.
 * - Editing the file in another editor updates this window too (unless you have unsaved
 *   changes here, in which case you are asked).
 */
import { For, Show, createEffect, createMemo, createSignal, on, type JSX } from "solid-js";
import type { ConfigIssue } from "@protocol/ConfigIssue";
import { useApp } from "../../app/context";
import { toast } from "../../shared/toast";

/** The config window. */
export function ConfigWindow(): JSX.Element {
  const { config } = useApp();
  const [text, setText] = createSignal("");
  const [baseline, setBaseline] = createSignal("");
  const [saveError, setSaveError] = createSignal<string | null>(null);
  const [changedOnDisk, setChangedOnDisk] = createSignal(false);
  let editor!: HTMLTextAreaElement;

  /** `true` when the editor differs from the file. */
  const dirty = () => text() !== baseline();
  const state = config.state;

  // Follow the file: replace the text unless the user has unsaved changes.
  createEffect(
    on(
      () => state()?.text,
      (fileText) => {
        if (fileText === undefined) return;
        if (!dirty() || text() === fileText) {
          setText(fileText);
          setBaseline(fileText);
          setChangedOnDisk(false);
        } else if (fileText !== baseline()) {
          setChangedOnDisk(true);
        }
      },
    ),
  );

  /** Selects line `line` (1-based) in the editor. */
  const goTo = (issue: ConfigIssue) => {
    const lines = text().split("\n");
    const start = lines.slice(0, issue.line - 1).reduce((n, l) => n + l.length + 1, 0);
    const end = start + (lines[issue.line - 1]?.length ?? 0);
    editor.focus();
    editor.setSelectionRange(start + Math.max(0, issue.column - 1), end);
    const lineHeight = parseFloat(getComputedStyle(editor).lineHeight) || 18;
    editor.scrollTop = Math.max(0, (issue.line - 4) * lineHeight);
  };

  /** Sends the text to the core, which checks it before writing the file. */
  const save = async () => {
    const error = await config.save(text());
    setSaveError(error);
    if (!error) {
      setBaseline(text());
      setChangedOnDisk(false);
      toast("Configuration saved and applied.");
    }
  };

  const status = createMemo(() => {
    const s = state();
    if (!s) return { kind: "wait", text: "Waiting for the core…" };
    if (s.errors.length) return { kind: "error", text: `${s.errors.length} error(s) in the file: the last valid configuration is still in use` };
    if (s.warnings.length) return { kind: "warn", text: `Applied, with ${s.warnings.length} warning(s)` };
    return { kind: "ok", text: "Applied, no problem found" };
  });

  return (
    <div class="configwin">
      <div class="cfg-head">
        <div class="cfg-path mono" title="The config file">
          {state()?.path ?? "in memory (no file)"}
        </div>
        <div class={`cfg-status ${status().kind}`} data-config-status={status().kind}>
          {status().text}
          <Show when={state()}>
            <span class="muted"> · revision {state()!.revision}</span>
          </Show>
        </div>
      </div>

      <Show when={(state()?.errors.length ?? 0) + (state()?.warnings.length ?? 0) > 0}>
        <ul class="issues" data-issues>
          <For each={state()?.errors ?? []}>
            {(issue) => (
              <li class="error">
                <button type="button" onClick={() => goTo(issue)}>
                  <b>line {issue.line}</b>:{issue.column} {issue.message}
                </button>
              </li>
            )}
          </For>
          <For each={state()?.warnings ?? []}>
            {(issue) => (
              <li class="warn">
                <button type="button" onClick={() => goTo(issue)}>
                  <b>line {issue.line}</b>:{issue.column} {issue.message}
                </button>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show when={changedOnDisk()}>
        <div class="cfg-notice">
          The file changed on disk while you were editing.
          <button type="button" class="mini" onClick={() => (setText(state()!.text), setBaseline(state()!.text), setChangedOnDisk(false))}>
            Load the file
          </button>
          <button type="button" class="mini" onClick={() => setChangedOnDisk(false)}>
            Keep my text
          </button>
        </div>
      </Show>

      <Show when={saveError()}>
        <div class="cfg-save-error" role="alert" data-save-error>
          Not saved. {saveError()}
        </div>
      </Show>

      <textarea
        class="cfg-editor"
        id="config-editor"
        ref={editor}
        spellcheck={false}
        value={text()}
        onInput={(e) => {
          setText(e.currentTarget.value);
          setSaveError(null);
        }}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
            e.preventDefault();
            void save();
          }
          if (e.key === "Tab") {
            e.preventDefault();
            const el = e.currentTarget;
            const at = el.selectionStart;
            el.setRangeText("    ", at, el.selectionEnd, "end");
            setText(el.value);
          }
        }}
      />

      <div class="btnrow cfg-actions">
        <button type="button" class="btn primary" id="config-save" disabled={!dirty()} title="Save (Ctrl+S)" onClick={() => void save()}>
          Save
        </button>
        <button type="button" class="btn" disabled={!dirty()} onClick={() => (setText(baseline()), setSaveError(null))}>
          Revert
        </button>
        <button type="button" class="btn" onClick={() => void config.reload().then(() => toast("Reloaded from disk."))}>
          Reload from disk
        </button>
        <button
          type="button"
          class="btn"
          title="Put the documented default configuration in the editor (not saved until you click Save)"
          onClick={() => void config.defaultText().then((t) => (setText(t), setSaveError(null)))}
        >
          Show default
        </button>
        <Show when={dirty()}>
          <span class="muted cfg-dirty">unsaved changes</span>
        </Show>
      </div>
    </div>
  );
}
